import React, { useCallback, useEffect, useState } from 'react';
import { api, uploadFile } from '../shared/api';

// m.4.1 StoneX - pomicni prosjek zlata na PMX racunu. Upload Deal Registera (PMX izvod), obracun se racuna na serveru pri svakom ucitavanju.

type Locale = 'hr' | 'en';
type Month = {
    month: string; realized_xau_eur: number; realized_xau_usd_eur: number; realized_xau_usd_usd: number; realized_total_eur: number;
    bought_oz: number; sold_oz: number; position_oz: number; position_cost_eur: number; position_avg_eur: number | null;
};
type Tx = {
    doc: string; trade_date: string; deal_type: string; pair: string; qty: number; price: number; hnb: number | null; price_eur: number;
    avg_before: number | null; realized_eur: number; realized_usd: number | null; position_oz: number; position_cost_eur: number; avg_after: number | null;
};
type Posting = { date: string; konto: string; debit: number; credit: number; usd: number | null; opis: string };
type Source = { source_file: string; deals: number; first: string; last: string; uploaded: string };
type UploadRow = { file: string; read?: number; inserted?: number; existing?: number; error?: string };
type Result = {
    months: Month[]; transactions: Tx[]; postings: Posting[]; missing_rates: string[]; sources: Source[]; method: string;
    position: { oz: number; cost_eur: number; avg_eur: number }; upload?: UploadRow[];
};

const nf = (n: number | null | undefined, d = 2) => (n === null || n === undefined ? '-' : new Intl.NumberFormat('hr-HR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n));
const hrDate = (s: string) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}.` : '');
const num: React.CSSProperties = { textAlign: 'right', fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' };
const box: React.CSSProperties = { border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, marginBottom: 14 };

export function StoneXAveragePanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Result | null>(null);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [uploadRows, setUploadRows] = useState<UploadRow[]>([]);
    const [month, setMonth] = useState('');

    const load = useCallback(async () => {
        try { setData(await api<Result>('/stonex/avg', token)); setErr(''); } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
    }, [token]);
    useEffect(() => { void load(); }, [load]);

    const onFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const fd = new FormData();
        Array.from(files).forEach((f) => fd.append('files[]', f));
        setBusy(true); setErr('');
        try {
            const r = await uploadFile<Result>('/stonex/avg/upload', token, fd);
            setUploadRows(r.upload ?? []); setData(r);
        } catch (e) { setErr(e instanceof Error ? e.message : 'Upload'); } finally { setBusy(false); }
    };

    const months2026 = (data?.months ?? []).filter((m) => m.month >= '2026');
    const tot = (k: keyof Month) => months2026.reduce((s, m) => s + (Number(m[k]) || 0), 0);
    const tx = (data?.transactions ?? []).filter((x) => !month || x.trade_date.startsWith(month));

    return (
        <div style={{ marginTop: 22 }}>
            <h3 style={{ marginBottom: 4 }}>{t('Pomični prosjek zlata (Deal Register)', 'Gold moving average (Deal Register)')}</h3>
            <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
                {t('Uploadaj mjesečni PMX izvod (xlsx s listom "Deal Register"). Isti izvod dvaput ne duplira transakcije. Obračun se računa iznova pri svakom učitavanju iz svih spremljenih transakcija.',
                    'Upload the monthly PMX statement (xlsx with a "Deal Register" sheet). Uploading the same file twice does not duplicate deals. The calculation is rebuilt from all stored deals on every load.')}
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <input type="file" accept=".xlsx,.xls" multiple disabled={busy} onChange={(e) => { void onFiles(e.target.files); e.target.value = ''; }} />
                {busy ? <span className="muted">{t('Učitavam…', 'Uploading…')}</span> : null}
            </div>
            {err ? <p className="error">{err}</p> : null}
            {uploadRows.length ? (
                <ul style={{ fontSize: '0.8rem', marginTop: 0 }}>
                    {uploadRows.map((u) => (
                        <li key={u.file}>{u.file}: {u.error ? <span className="error">{u.error}</span>
                            : t(`pročitano ${u.read}, novih ${u.inserted}, već postoji ${u.existing}`, `read ${u.read}, new ${u.inserted}, existing ${u.existing}`)}</li>
                    ))}
                </ul>
            ) : null}
            {data?.missing_rates.length ? <p className="error">{t('Nema tečaja HNB za', 'Missing HNB rate for')}: {data.missing_rates.join(', ')}</p> : null}

            {data ? (
                <>
                    <p style={{ fontSize: '0.84rem' }}>
                        {t('Stanje vreće', 'Position')}: <strong>{nf(data.position.oz, 3)} oz</strong>, {t('trošak', 'cost')} <strong>{nf(data.position.cost_eur)} EUR</strong>,
                        {' '}{t('prosjek', 'average')} <strong>{nf(data.position.avg_eur, 2)} EUR/oz</strong>
                    </p>
                    <div className="table-wrap" style={box}>
                        <table className="admin-user-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                            <thead><tr>
                                <th>{t('Mjesec', 'Month')}</th><th style={num}>XAU-EUR</th><th style={num}>XAU-USD (EUR)</th><th style={num}>XAU-USD (USD)</th>
                                <th style={num}>{t('Ukupno EUR', 'Total EUR')}</th><th style={num}>{t('Kupljeno oz', 'Bought oz')}</th><th style={num}>{t('Prodano oz', 'Sold oz')}</th>
                                <th style={num}>{t('Vreća oz', 'Position oz')}</th><th style={num}>{t('Trošak vreće', 'Position cost')}</th><th style={num}>{t('Prosjek', 'Average')}</th>
                            </tr></thead>
                            <tbody>
                                {data.months.map((m) => (
                                    <tr key={m.month} onClick={() => setMonth(month === m.month ? '' : m.month)} style={{ cursor: 'pointer', fontWeight: month === m.month ? 600 : undefined }}>
                                        <td>{m.month}</td><td style={num}>{nf(m.realized_xau_eur)}</td><td style={num}>{nf(m.realized_xau_usd_eur)}</td><td style={num}>{nf(m.realized_xau_usd_usd)}</td>
                                        <td style={num}>{nf(m.realized_total_eur)}</td><td style={num}>{nf(m.bought_oz, 3)}</td><td style={num}>{nf(m.sold_oz, 3)}</td>
                                        <td style={num}>{nf(m.position_oz, 3)}</td><td style={num}>{nf(m.position_cost_eur)}</td><td style={num}>{nf(m.position_avg_eur)}</td>
                                    </tr>
                                ))}
                                <tr style={{ fontWeight: 600 }}>
                                    <td>{t('Ukupno 2026', 'Total 2026')}</td><td style={num}>{nf(tot('realized_xau_eur'))}</td><td style={num}>{nf(tot('realized_xau_usd_eur'))}</td>
                                    <td style={num}>{nf(tot('realized_xau_usd_usd'))}</td><td style={num}>{nf(tot('realized_total_eur'))}</td><td colSpan={5} />
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h4 style={{ margin: '6px 0' }}>{t('Prijedlog knjiženja', 'Proposed postings')}{month ? ` ${month}` : ''}</h4>
                    <div className="table-wrap" style={box}>
                        <table className="admin-user-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                            <thead><tr><th>{t('Datum', 'Date')}</th><th>Konto</th><th style={num}>{t('Duguje EUR', 'Debit EUR')}</th><th style={num}>{t('Potražuje EUR', 'Credit EUR')}</th><th style={num}>USD (840)</th><th>{t('Opis', 'Description')}</th></tr></thead>
                            <tbody>
                                {data.postings.filter((p) => !month || p.date.startsWith(month)).map((p, i) => (
                                    <tr key={i}><td>{hrDate(p.date)}</td><td>{p.konto}</td><td style={num}>{p.debit ? nf(p.debit) : ''}</td><td style={num}>{p.credit ? nf(p.credit) : ''}</td><td style={num}>{p.usd ? nf(p.usd) : ''}</td><td>{p.opis}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h4 style={{ margin: '6px 0' }}>{t('Transakcije red po red', 'Deals row by row')}{month ? ` ${month}` : ` (${t('klikni mjesec za filter', 'click a month to filter')})`}</h4>
                    <div className="table-wrap" style={{ ...box, maxHeight: 520, overflow: 'auto' }}>
                        <table className="admin-user-table" style={{ width: '100%', fontSize: '0.78rem' }}>
                            <thead><tr>
                                <th>{t('Datum', 'Date')}</th><th>{t('Dokument', 'Doc')}</th><th>{t('Vrsta', 'Type')}</th><th>Par</th><th style={num}>oz</th><th style={num}>{t('Cijena', 'Price')}</th>
                                <th style={num}>HNB</th><th style={num}>{t('Cijena EUR', 'Price EUR')}</th><th style={num}>{t('Prosjek prije', 'Avg before')}</th><th style={num}>{t('Rezultat EUR', 'Result EUR')}</th>
                                <th style={num}>{t('Vreća oz', 'Position oz')}</th><th style={num}>{t('Trošak vreće', 'Position cost')}</th><th style={num}>{t('Prosjek nakon', 'Avg after')}</th>
                            </tr></thead>
                            <tbody>
                                {tx.map((x) => (
                                    <tr key={x.doc}>
                                        <td>{hrDate(x.trade_date)}</td><td>{x.doc}</td><td>{x.deal_type === 'BUY' ? t('kupnja', 'buy') : t('prodaja', 'sell')}</td><td>{x.pair}</td>
                                        <td style={num}>{nf(x.qty, 3)}</td><td style={num}>{nf(x.price)}</td><td style={num}>{x.hnb ? nf(x.hnb, 4) : ''}</td><td style={num}>{nf(x.price_eur)}</td>
                                        <td style={num}>{nf(x.avg_before)}</td><td style={num}>{x.deal_type === 'SELL' || x.realized_eur ? nf(x.realized_eur) : ''}</td>
                                        <td style={num}>{nf(x.position_oz, 3)}</td><td style={num}>{nf(x.position_cost_eur)}</td><td style={num}>{nf(x.avg_after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="muted" style={{ fontSize: '0.76rem', fontStyle: 'italic' }}>{data.method}</p>
                    {data.sources.length ? (
                        <p className="muted" style={{ fontSize: '0.76rem' }}>
                            {t('Izvori', 'Sources')}: {data.sources.map((s) => `${s.source_file} (${s.deals}, ${hrDate(s.first)}-${hrDate(s.last)})`).join('; ')}
                        </p>
                    ) : <p className="muted">{t('Još nema uploadanih izvoda.', 'No statements uploaded yet.')}</p>}
                </>
            ) : null}
        </div>
    );
}
